import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/api/client'
import { Teacher } from '@/types/teacher-request'

interface TeacherSearchDropdownProps {
  excludeTeacherIds?: string[]
  onSelect: (teacher: Teacher) => void
  onClose: () => void
  align?: 'left' | 'right'
}

interface TeacherSearchResult {
  id: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  teacher_code: string | null
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debouncedValue
}

export function TeacherSearchDropdown({ excludeTeacherIds = [], onSelect, onClose, align = 'left' }: TeacherSearchDropdownProps) {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { data: teachers = [], error, isLoading } = useQuery<TeacherSearchResult[]>({
    queryKey: ['teacher-search', debouncedQuery],
    queryFn: async () => {
      if (debouncedQuery.length < 2) return []
      const response = await apiClient.get('/api/v1/teacher/search', { params: { q: debouncedQuery, limit: 10 } })
      return response.data
    },
    enabled: debouncedQuery.length >= 2,
  })

  const filteredTeachers = teachers.filter(t => !excludeTeacherIds.includes(t.id))

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div
      ref={dropdownRef}
      className={`absolute top-full mt-1 w-72 max-w-[calc(100vw-2rem)] bg-white border rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto ${align === 'right' ? 'right-0' : 'left-0'}`}
    >
      <div className="p-2 border-b">
        <input
          type="text"
          aria-label="Lehrer suchen"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Name, Kürzel oder E-Mail..."
          className="w-full min-w-0 px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500"
          autoFocus
        />
      </div>
      {query.length >= 2 && isLoading && (
        <div className="p-3 text-sm text-gray-500 text-center">Suche...</div>
      )}
      {query.length >= 2 && error && (
        <div className="p-3 text-sm text-red-500 text-center">Fehler beim Laden</div>
      )}
      {query.length >= 2 && !isLoading && !error && filteredTeachers.length === 0 && (
        <div className="p-3 text-sm text-gray-500 text-center">Keine Lehrer gefunden</div>
      )}
      {filteredTeachers.map(teacher => (
        <button
          key={teacher.id}
          onClick={() => {
            onSelect(teacher as unknown as Teacher)
            onClose()
          }}
          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
        >
          <div className="text-sm font-medium text-gray-800 truncate">
            {teacher.last_name} {teacher.first_name}
            {teacher.teacher_code ? (
              <span className="ml-2 text-xs font-mono font-normal text-gray-500">{teacher.teacher_code}</span>
            ) : null}
          </div>
          <div className="text-xs text-gray-500 truncate">{teacher.email}</div>
        </button>
      ))}
    </div>
  )
}
